// Lab Orders — expandable Lab → Clinician → Patient → Order tree, with a
// click-through to a full "digital requisition" view (the same
// DynamicRequisitionForm the clinician used to fill it in, rendered
// read-only) instead of just a PDF link.
//
// ONE shared component backs BOTH surfaces the founder asked for:
//   - admin (isAdmin=true)  — cross-lab, reads /api/admin/order-records*,
//     admin_token auth, shows the Lab level of the tree, has a lab filter.
//   - staff (isAdmin=false) — reads /api/staff/lab-orders*, regular staff
//     `token` auth, server-side FORCED to the logged-in user's own lab
//     (see labariumaireport/backend/main.py's staff_list_lab_orders — no
//     lab param exists on that call at all), so the Lab level of the tree
//     is omitted here since it's always exactly one lab.
//
// No MUI TreeView dependency in this frontend's package.json — this uses
// simple nested collapsible Boxes (same "bordered card" visual language
// as IdentityLinking.js/AuditDashboard.js) rather than adding one.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import axios from "axios";
import api from "../api";
import { extractErrorDetail } from "./validators";
import {
  Box, Typography, TextField, Button, Alert, CircularProgress, Container,
  IconButton, Tooltip, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  Select, MenuItem, Tabs, Tab, Badge,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import ScienceIcon from "@mui/icons-material/Science";
import AdminPageShell from "./AdminPageShell";
import DynamicRequisitionForm from "./DynamicRequisitionForm";
import UploadProoveReport from "./UploadProoveReport";
import { formatEST } from "../dateUtils";

const API = process.env.REACT_APP_API_URL || "http://localhost:8000";
const BRAND_NAVY = "#00357a";

// Section ids that are shared across every requisition in the same order
// (identical "ordering_provider"/"patient_info"/"payment_info" sections,
// byte-identical submitted_data on every requisition — confirmed this
// session) — same exclusion list labariumaiportal/frontend's OrderTest.js
// (the REAL clinician-facing form) uses to keep these from being re-shown
// per family. Recreated here identically rather than imported since this
// is a separate frontend package from labariumaiportal's.
const SHARED_SECTION_IDS = ["ordering_provider", "patient_info", "payment_info"];

// 2026-10-12 fix — order rows/requisition headers showed raw test_id codes
// (e.g. "proove_pgx", "tox_placeholder_1") instead of a readable name.
// Mirrors the display names already defined server-side in
// labariumaiportal/backend/main.py's orderable_tests_collection seed list
// (~line 759-796) — duplicated here as a plain inline dict, same
// recreate-rather-than-cross-import convention SHARED_SECTION_IDS above
// already uses for this separate frontend package. Falls back to the raw
// id for anything not in this list rather than hiding it.
const TEST_ID_DISPLAY_NAMES = {
  proove_pgx: "Proove Drug Metabolism™ Profile (PGx)",
  proove_opioid_risk: "Proove Opioid Risk™ Profile",
  proove_pain_perception: "Proove Pain Perception™ Profile",
  tox_placeholder_1: "Toxicology Screening Panel",
  tox_placeholder_2: "Toxicology Confirmation Panel",
  id_molecular_placeholder_1: "Infectious Disease / Molecular PCR Panel",
  id_molecular_placeholder_2: "Infectious Disease Reference Culture",
};

// 2026-10-12 fix — order status showed the raw backend value ("finalized")
// with no plain-language meaning to staff/admin. Per-requisition values
// (clinician_signed/patient_signed) included for completeness/future-
// proofing even though this app's order-level `status` field is only
// ever "finalized" today.
const ORDER_STATUS_DISPLAY = {
  clinician_signed: "Awaiting Clinician Signature",
  patient_signed: "Awaiting Patient Signature",
  finalized: "Order Submitted",
};

// Supporting Documents — mirrors the backend's SUPPORTING_DOC_TYPES
// allowlist (labariumaireport/backend/main.py, same set
// labariumaiportal/backend/main.py's clinician-side upload uses).
const SUPPORTING_DOC_TYPE_OPTIONS = [
  { value: "patient_id", label: "Patient ID" },
  { value: "insurance_card", label: "Insurance Card" },
  { value: "drivers_license", label: "Driver's License" },
  { value: "abn", label: "ABN" },
  { value: "pa", label: "PA — Prior Auth" },
  { value: "other", label: "Other" },
];
const SUPPORTING_DOC_TYPE_LABELS = Object.fromEntries(SUPPORTING_DOC_TYPE_OPTIONS.map((o) => [o.value, o.label]));

// Same accent-bar + bold-title section-header convention IdentityLinking.js
// uses for every admin card section header in this app (3px navy bar +
// 600-weight title), applied here for visual consistency.
function SectionHeader({ children }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
      <Box sx={{ width: 3, height: 18, backgroundColor: BRAND_NAVY, borderRadius: 1 }} />
      <Typography sx={{ fontWeight: 600, fontSize: "0.95rem", color: BRAND_NAVY }}>{children}</Typography>
    </Box>
  );
}

function adminAuthHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem("admin_token")}` };
}

export default function LabOrdersView({ isAdmin }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tokenKey = isAdmin ? "admin_token" : "token";
  const loginPath = isAdmin ? "/admin-login" : "/login";
  const backPath = isAdmin ? "/admin" : "/";
  const base = isAdmin ? "/api/admin/order-records" : "/api/staff/lab-orders";

  const [filters, setFilters] = useState({
    lab: "", patient_name: "", dob: "", date_from: "", date_to: "",
    npi: "", test_id: "", family: "", order_id: "", status: "",
  });
  const [tree, setTree] = useState({ labs: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState({}); // key -> bool, key = "lab" | "lab|clinSub" | "lab|clinSub|patientId"
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");

  // Saved Test Results — admin-only (backend route is admin-only, see
  // /api/admin/patient-records/{patient_id}/test-results's own comment on
  // why this isn't mirrored to staff). testResultsPatient holds the
  // patient label shown in the dialog title; null means closed.
  const [testResultsPatient, setTestResultsPatient] = useState(null);
  const [testResultsRows, setTestResultsRows] = useState([]);
  const [testResultsLoading, setTestResultsLoading] = useState(false);
  const [testResultsError, setTestResultsError] = useState("");
  const testResultsRequestIdRef = useRef(null);

  // "Awaiting Proove Report" queue tab — a SEPARATE fetch from the
  // lab-orders tree above (its own endpoint, its own shape: a flat list
  // of orders, not a Lab/Clinician/Patient tree), same isAdmin/base
  // switch every other call in this component already uses.
  const [activeTab, setActiveTab] = useState(0); // 0 = Orders, 1 = Awaiting Proove Report
  const [queueRows, setQueueRows] = useState([]);
  const [queueLoading, setQueueLoading] = useState(false);
  const [queueError, setQueueError] = useState("");
  const [queueExpanded, setQueueExpanded] = useState({}); // order_id -> bool
  const [queueFetched, setQueueFetched] = useState(false); // so switching tabs doesn't re-fetch once we've loaded it eagerly

  // Proove Report Upload — staff-only (POST /api/proove-reports/upload
  // requires a staff bearer token, not admin), so this whole feature is
  // gated to the staff surface (!isAdmin). `uploadContext` holds either
  // an order-derived context object (Path A, opened from a queue row or
  // the OrderDetailDialog banner) or null (Path B, the standalone manual
  // entry point reached via the page-level button below).
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadContext, setUploadContext] = useState(null);

  const doFetch = useCallback(async (overrideFilters) => {
    const f = overrideFilters || filters;
    setLoading(true); setError("");
    try {
      const params = {};
      if (isAdmin && f.lab) params.lab = f.lab;
      if (f.patient_name) params.patient_name = f.patient_name;
      if (f.dob) params.dob = f.dob;
      if (f.date_from) params.date_from = f.date_from;
      if (f.date_to) params.date_to = f.date_to;
      if (f.npi) params.npi = f.npi;
      if (f.test_id) params.test_id = f.test_id;
      if (f.family) params.family = f.family;
      if (f.order_id) params.order_id = f.order_id;
      if (f.status) params.status = f.status;

      const res = isAdmin
        ? await axios.get(`${API}${base}`, { headers: adminAuthHeaders(), params })
        : await api.get(base, { params });

      if (res.data.error) setError(res.data.error);
      setTree(res.data);
    } catch (err) {
      if (err?.response?.status === 401) { localStorage.removeItem(tokenKey); navigate(loginPath); return; }
      setError(extractErrorDetail(err, "Failed to load Lab Orders."));
    } finally {
      setLoading(false);
    }
  }, [filters, isAdmin, base, tokenKey, loginPath, navigate]);

  useEffect(() => {
    if (!localStorage.getItem(tokenKey)) { navigate(loginPath); return; }
    doFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchQueue = useCallback(async () => {
    setQueueLoading(true); setQueueError("");
    try {
      const res = isAdmin
        ? await axios.get(`${API}${base}/awaiting-proove-report`, { headers: adminAuthHeaders() })
        : await api.get(`${base}/awaiting-proove-report`);
      if (res.data.error) setQueueError(res.data.error);
      setQueueRows(res.data.orders || []);
      // Only mark "fetched" on a successful response — a transient error on
      // the eager mount-time fetch should still let the user retry by
      // switching to the tab, rather than being stuck showing that error
      // forever with no further fetch attempt.
      setQueueFetched(true);
    } catch (err) {
      if (err?.response?.status === 401) { localStorage.removeItem(tokenKey); navigate(loginPath); return; }
      setQueueError(extractErrorDetail(err, "Failed to load the Awaiting Proove Report queue."));
    } finally {
      setQueueLoading(false);
    }
  }, [isAdmin, base, tokenKey, loginPath, navigate]);

  // Fetch the queue once at page load (not just on tab-switch) so the
  // "Awaiting Proove Report" tab's count badge (see the Tabs block below)
  // is accurate even before that tab is ever opened. `queueFetched` then
  // guards against a redundant re-fetch when the user actually switches
  // to tab 1 — same list this effect already loaded is reused.
  useEffect(() => {
    if (!localStorage.getItem(tokenKey)) return;
    fetchQueue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeTab === 1 && !queueFetched && !queueLoading) fetchQueue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const toggle = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  const toggleQueueRow = (orderId) => setQueueExpanded((prev) => ({ ...prev, [orderId]: !prev[orderId] }));

  // Proove Report Upload — Path A entry points. Both the queue row's own
  // "Upload Report" action and the OrderDetailDialog's banner button
  // build the same shape of order-context object for UploadProoveReport,
  // just from two different already-loaded data shapes (a flat queue row
  // vs. the full order detail).
  const openUploadFromQueueRow = (o, e) => {
    if (e) e.stopPropagation();
    setUploadContext({
      order_id: o.order_id,
      proove_test_ids: o.proove_test_ids || [],
      patient: {
        first_name: o.patient_first_name, last_name: o.patient_last_name, dob: o.patient_dob,
        address_line1: o.patient_address_line1, address_unit: o.patient_address_unit,
        address_city: o.patient_address_city, address_state: o.patient_address_state, address_zip: o.patient_address_zip,
        // 2026-10-22 (Piece 5) — prefills UploadProoveReport.js's now-
        // mandatory Patient Email field from the order's own patient
        // record, same as every other identity field here.
        email: o.patient_email,
      },
      practice_name: o.practice_name, npi: o.npi,
      provider_name: [o.clinician_first_name, o.clinician_last_name].filter(Boolean).join(" "),
    });
    setUploadOpen(true);
  };

  const openUploadFromDetail = () => {
    if (!detail || !detail.patient) return;
    const prooveIds = (detail.test_ids || []).filter((tid) => TEST_ID_DISPLAY_NAMES[tid] && tid.startsWith("proove_"));
    setUploadContext({
      order_id: detail.order_id,
      proove_test_ids: prooveIds,
      patient: detail.patient,
      practice_name: detail.practice_name, npi: detail.npi, provider_name: detail.provider_name,
    });
    setUploadOpen(true);
  };

  const openManualUpload = () => {
    setUploadContext(null);
    setUploadOpen(true);
  };

  // After a successful upload: the order detail dialog (if open) and the
  // queue's own counts/rows are both now stale — re-fetch whichever is
  // actually in view, same "re-fetch after mutation" discipline the
  // Supporting Documents upload above already uses.
  const onProoveReportUploaded = () => {
    if (uploadContext?.order_id) {
      if (detail?.order_id === uploadContext.order_id) openOrder(uploadContext.order_id);
      fetchQueue();
    }
  };

  const openOrder = async (orderId) => {
    setDetail({ order_id: orderId });
    setDetailLoading(true); setDetailError("");
    try {
      const res = isAdmin
        ? await axios.get(`${API}${base}/${orderId}`, { headers: adminAuthHeaders() })
        : await api.get(`${base}/${orderId}`);
      setDetail(res.data);
    } catch (err) {
      setDetailError(extractErrorDetail(err, "Failed to load order detail."));
    } finally {
      setDetailLoading(false);
    }
  };

  const downloadPdf = async (orderId, schemaKey) => {
    try {
      const res = isAdmin
        ? await axios.get(`${API}${base}/${orderId}/pdf-url`, { headers: adminAuthHeaders(), params: { schema_key: schemaKey } })
        : await api.get(`${base}/${orderId}/pdf-url`, { params: { schema_key: schemaKey } });
      if (res.data.url) window.open(res.data.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      // best-effort — a failed signed-url shouldn't block the rest of the page
      window.alert(extractErrorDetail(err, "Could not generate a download link right now."));
    }
  };

  // Inline preview pane (OrderDetailDialog) — SAME pdf-url endpoint
  // downloadPdf uses just above, just with ?disposition=inline so the
  // browser renders the PDF instead of downloading it (see main.py's
  // admin_get_order_record_pdf_url/staff_get_lab_order_pdf_url — the
  // disposition param defaults to "attachment" there, so downloadPdf's
  // own behavior above is completely unchanged by this addition).
  const previewPdfUrl = async (orderId, schemaKey) => {
    const params = { schema_key: schemaKey, disposition: "inline" };
    const res = isAdmin
      ? await axios.get(`${API}${base}/${orderId}/pdf-url`, { headers: adminAuthHeaders(), params })
      : await api.get(`${base}/${orderId}/pdf-url`, { params });
    return res.data.url;
  };

  // Supporting Documents — staff/admin can add docs post-receipt (per the
  // founder's product decision, visible to the clinician too, no role-
  // based hiding). Re-fetches the open detail dialog's data after a
  // successful upload so the newly-added doc shows up immediately.
  const uploadSupportingDoc = async (orderId, { docType, otherLabel, file }) => {
    const formData = new FormData();
    formData.append("doc_type", docType);
    if (docType === "other" && otherLabel) formData.append("doc_type_other_label", otherLabel);
    formData.append("files", file);
    if (isAdmin) {
      await axios.post(`${API}${base}/${orderId}/supporting-docs`, formData, { headers: adminAuthHeaders() });
    } else {
      await api.post(`${base}/${orderId}/supporting-docs`, formData);
    }
    await openOrder(orderId);
  };

  const getSupportingDocUrl = async (orderId, docIndex) => {
    const res = isAdmin
      ? await axios.get(`${API}${base}/${orderId}/supporting-docs/${docIndex}/url`, { headers: adminAuthHeaders() })
      : await api.get(`${base}/${orderId}/supporting-docs/${docIndex}/url`);
    return res.data.url;
  };

  // Saved Test Results dialog — admin-only, opened from a Patient tree
  // node (see TreeRow below). label is the "First Last — DOB ..." string
  // already shown on that row, reused as the dialog title so the admin
  // doesn't lose track of which patient they're looking at.
  //
  // ui-ux-checker finding (real, net-new to this piece) — the title is
  // set synchronously on click but the rows arrive later with no request
  // tagging, so opening patient A's dialog, closing it, then opening
  // patient B's before A's slower request resolves could land A's rows
  // under B's already-displayed title: a wrong-patient PHI display, not
  // just a stale-but-self-consistent one (unlike openOrder's pre-existing
  // race above, where the title comes FROM the response itself).
  // testResultsRequestIdRef tags each request so a late response for a
  // patient that's no longer the open one is simply discarded.
  const openPatientTestResults = async (patientId, label, e) => {
    if (e) e.stopPropagation();
    const requestId = patientId;
    testResultsRequestIdRef.current = requestId;
    setTestResultsPatient(label);
    setTestResultsLoading(true); setTestResultsError(""); setTestResultsRows([]);
    try {
      const res = await axios.get(`${API}/api/admin/patient-records/${patientId}/test-results`, { headers: adminAuthHeaders() });
      if (testResultsRequestIdRef.current !== requestId) return; // superseded by a newer open — discard
      setTestResultsRows(res.data.results || []);
    } catch (err) {
      if (testResultsRequestIdRef.current !== requestId) return;
      if (err?.response?.status === 401) { localStorage.removeItem(tokenKey); navigate(loginPath); return; }
      setTestResultsError(extractErrorDetail(err, "Failed to load this patient's saved test results."));
    } finally {
      if (testResultsRequestIdRef.current === requestId) setTestResultsLoading(false);
    }
  };

  const logout = () => { localStorage.removeItem(tokenKey); navigate(loginPath); };

  const labs = isAdmin ? (tree.labs || []) : (tree.labs || []); // staff response is the same shape, just always exactly one lab entry

  return (
    <AdminPageShell
      isDark={isDark}
      backPath={backPath}
      onLogout={logout}
      title="Lab Orders"
      subtitle={
        activeTab === 1
          ? "Orders waiting on a manually uploaded Proove report"
          : isAdmin
            ? "Every submitted order, across every lab — Lab → Clinician → Patient → Order"
            : "Every order submitted for your lab — Clinician → Patient → Order"
      }
      actions={
        /* Proove Report Upload, Path B standalone entry point — staff
           only (the upload endpoint requires a staff bearer token, not
           admin). Reached without any order context, for a paper-
           requisition case that has no corresponding queue row. */
        !isAdmin && (
          <Button size="small" variant="outlined" onClick={openManualUpload} sx={{ textTransform: "none", whiteSpace: "nowrap" }}>
            Upload Proove Report
          </Button>
        )
      }
    >
      <Container maxWidth="md">
        {/* Tabs — same two-surface (isAdmin/staff) component, so both the
            tree view and the new queue work unmodified on either surface. */}
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 3, minHeight: 36 }}
          TabIndicatorProps={{ sx: { bgcolor: BRAND_NAVY } }}>
          <Tab label="Orders" sx={{ textTransform: "none", minHeight: 36, fontWeight: 600 }} />
          <Tab
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                Awaiting Proove Report
                <Badge
                  badgeContent={queueRows.length}
                  color="warning"
                  sx={{ "& .MuiBadge-badge": { position: "relative", transform: "none" } }}
                />
              </Box>
            }
            sx={{ textTransform: "none", minHeight: 36, fontWeight: 600 }}
          />
        </Tabs>

        {activeTab === 0 && (
          <>
            {error && <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

            {/* Filters — same input-row styling as AuditDashboard.js's filter row */}
            <Box sx={{ backgroundColor: "#FFF", border: "1px solid #E8E8E8", borderRadius: 2, p: 2.5, mb: 3, display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
              {isAdmin && (
                <TextField size="small" label="Lab" value={filters.lab}
                  onChange={(e) => setFilters((f) => ({ ...f, lab: e.target.value }))} sx={{ width: 160 }} />
              )}
              <TextField size="small" label="Patient Name" value={filters.patient_name}
                onChange={(e) => setFilters((f) => ({ ...f, patient_name: e.target.value }))} sx={{ width: 180 }} />
              <TextField size="small" label="DOB (YYYY-MM-DD)" value={filters.dob}
                onChange={(e) => setFilters((f) => ({ ...f, dob: e.target.value }))} sx={{ width: 160 }} />
              <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={filters.date_from}
                onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))} sx={{ width: 150 }} />
              <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={filters.date_to}
                onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))} sx={{ width: 150 }} />
              <TextField size="small" label="Clinician NPI" value={filters.npi}
                onChange={(e) => setFilters((f) => ({ ...f, npi: e.target.value }))} sx={{ width: 140 }} />
              <TextField size="small" label="Test ID" value={filters.test_id}
                onChange={(e) => setFilters((f) => ({ ...f, test_id: e.target.value }))} sx={{ width: 150 }} />
              <TextField size="small" label="Family" placeholder="proove / toxicology / ..." value={filters.family}
                onChange={(e) => setFilters((f) => ({ ...f, family: e.target.value }))} sx={{ width: 170 }} />
              <TextField size="small" label="Order ID" value={filters.order_id}
                onChange={(e) => setFilters((f) => ({ ...f, order_id: e.target.value }))} sx={{ width: 150 }} />
              <TextField size="small" label="Status" value={filters.status}
                onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} sx={{ width: 130 }} />
              <Button variant="contained" size="small" onClick={doFetch} sx={{ bgcolor: BRAND_NAVY, "&:hover": { bgcolor: "#002a5c" } }}>Apply</Button>
              <Button variant="outlined" size="small" onClick={() => {
                const cleared = { lab: "", patient_name: "", dob: "", date_from: "", date_to: "", npi: "", test_id: "", family: "", order_id: "", status: "" };
                setFilters(cleared);
                doFetch(cleared);
              }}>Reset</Button>
            </Box>

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress sx={{ color: BRAND_NAVY }} /></Box>
            ) : labs.length === 0 ? (
              <Box sx={{ backgroundColor: "#FFF", border: "1px solid #E8E8E8", borderRadius: 2, p: 4, textAlign: "center" }}>
                <Typography sx={{ color: "#AAA", fontSize: "0.9rem" }}>No orders match these filters.</Typography>
              </Box>
            ) : (
              labs.map((labNode) => (
                <Box key={labNode.lab} sx={{ backgroundColor: "#FFF", border: "1px solid #E8E8E8", borderRadius: 2, mb: 2, overflow: "hidden" }}>
                  {isAdmin && (
                    <TreeRow depth={0} onClick={() => toggle(labNode.lab)} expanded={!!expanded[labNode.lab]}
                      label={labNode.lab} count={labNode.count} bold />
                  )}
                  {(!isAdmin || expanded[labNode.lab]) && labNode.clinicians.map((clin) => {
                    const clinKey = `${labNode.lab}|${clin.clinician_sub}`;
                    return (
                      <Box key={clinKey}>
                        <TreeRow depth={isAdmin ? 1 : 0} onClick={() => toggle(clinKey)} expanded={!!expanded[clinKey]}
                          label={`${clin.practice_name || "Unknown Practice"} (NPI ${clin.npi || "—"})`} count={clin.count} />
                        {expanded[clinKey] && clin.patients.map((pat) => {
                          const patKey = `${clinKey}|${pat.patient_id}`;
                          const patLabel = `${pat.first_name} ${pat.last_name} — DOB ${pat.dob || "—"}`;
                          return (
                            <Box key={patKey}>
                              <TreeRow depth={isAdmin ? 2 : 1} onClick={() => toggle(patKey)} expanded={!!expanded[patKey]}
                                label={patLabel} count={pat.orders.length}
                                extraAction={isAdmin && (
                                  <Tooltip title="View this patient's saved test results (any lab)">
                                    <Button size="small" startIcon={<ScienceIcon sx={{ fontSize: 14 }} />}
                                      onClick={(e) => openPatientTestResults(pat.patient_id, patLabel, e)}
                                      sx={{ textTransform: "none", fontSize: 11.5, color: BRAND_NAVY, flexShrink: 0, py: 0.25, minHeight: 0 }}>
                                      Test Results
                                    </Button>
                                  </Tooltip>
                                )} />
                              {expanded[patKey] && pat.orders.map((o) => (
                                <Box key={o.order_id} onClick={() => openOrder(o.order_id)}
                                  sx={{
                                    display: "flex", alignItems: "center", justifyContent: "space-between",
                                    pl: (isAdmin ? 3 : 2) + 2, pr: 2, py: 1, cursor: "pointer",
                                    borderTop: "1px solid #F4F4F5", "&:hover": { backgroundColor: "#FAFAFA" },
                                  }}>
                                  <Typography sx={{ fontSize: "0.82rem", color: "#333" }}>
                                    Order {o.order_id} &mdash; {(o.test_ids || []).map((tid) => TEST_ID_DISPLAY_NAMES[tid] || tid.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())).join(", ")}
                                  </Typography>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    {(o.families || []).map((fam) => (
                                      <Chip key={fam} size="small" label={fam} sx={{ fontSize: "0.65rem", height: 20, bgcolor: "#FFF4E0", color: "#8a5a00" }} />
                                    ))}
                                    <Chip size="small" label={ORDER_STATUS_DISPLAY[o.status] || o.status} sx={{ fontSize: "0.68rem", height: 20, bgcolor: "#F0F4FF", color: "#1A3A7D" }} />
                                    <Typography sx={{ fontSize: "0.75rem", color: "#888" }}>
                                      {o.submitted_at ? formatEST(o.submitted_at, { dateOnly: true }) : "—"}
                                    </Typography>
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                          );
                        })}
                      </Box>
                    );
                  })}
                </Box>
              ))
            )}
          </>
        )}

        {activeTab === 1 && (
          <AwaitingProoveReportQueue
            rows={queueRows} loading={queueLoading} error={queueError}
            expanded={queueExpanded} onToggleRow={toggleQueueRow}
            onOpenOrder={openOrder}
            onUploadReport={!isAdmin ? openUploadFromQueueRow : null}
          />
        )}
      </Container>

      <OrderDetailDialog
        open={!!detail} onClose={() => setDetail(null)} detail={detail}
        loading={detailLoading} error={detailError} onDownloadPdf={downloadPdf}
        onPreviewPdf={previewPdfUrl}
        onUploadSupportingDoc={uploadSupportingDoc} onGetSupportingDocUrl={getSupportingDocUrl}
        onUploadProoveReport={!isAdmin ? openUploadFromDetail : null}
      />

      <UploadProoveReport
        open={uploadOpen} onClose={() => setUploadOpen(false)}
        orderContext={uploadContext} onUploaded={onProoveReportUploaded}
      />

      {isAdmin && (
        <PatientTestResultsDialog
          open={!!testResultsPatient} onClose={() => setTestResultsPatient(null)}
          patientLabel={testResultsPatient} rows={testResultsRows}
          loading={testResultsLoading} error={testResultsError}
        />
      )}
    </AdminPageShell>
  );
}

function TreeRow({ depth, onClick, expanded, label, count, bold, extraAction }) {
  return (
    <Box onClick={onClick} sx={{
      display: "flex", alignItems: "center", gap: 1, cursor: "pointer",
      pl: 2 + depth * 2, pr: 2, py: 1.25,
      borderTop: depth === 0 ? "none" : "1px solid #F4F4F5",
      "&:hover": { backgroundColor: "#FAFAFA" },
    }}>
      {expanded ? <ExpandMoreIcon sx={{ fontSize: 18, color: BRAND_NAVY }} /> : <ChevronRightIcon sx={{ fontSize: 18, color: BRAND_NAVY }} />}
      <Typography sx={{ fontSize: depth === 0 ? "0.95rem" : "0.85rem", fontWeight: bold ? 700 : 600, color: "#111", flex: 1 }}>{label}</Typography>
      {extraAction}
      <Chip size="small" label={count} sx={{ fontSize: "0.68rem", height: 20, bgcolor: "#F4F4F5", color: "#555" }} />
    </Box>
  );
}

// ─── Awaiting Proove Report queue ──────────────────────────────────────────
// Flat list (not the Lab/Clinician/Patient tree the Orders tab uses) —
// one row per order with awaiting_proove_report=true/proove_report_
// delivered!=true (see labariumaireport/backend/main.py's
// _awaiting_proove_report_queue). Each row shows the rich snapshot asked
// for directly (patient/clinician/test/submitted/days-waiting), with a
// click-to-expand (same TreeRow chevron-expand interaction already used
// by the Orders tab above, rather than inventing a hover tooltip) for the
// fuller confirm-it's-the-right-order detail — full address, NPI, and any
// other tests on the same order.
//
// 2026-10-12 fix — interaction was reversed vs. the Orders tab: the whole
// row toggled the inline expand and a separate "Open Order" button opened
// the dialog. Now clicking the row body opens the order (consistent with
// the Orders tab's click-to-open rows), and a dedicated chevron
// IconButton (stopPropagation'd) toggles the inline expand instead.
function AwaitingProoveReportQueue({ rows, loading, error, expanded, onToggleRow, onOpenOrder, onUploadReport }) {
  if (loading) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress sx={{ color: BRAND_NAVY }} /></Box>;
  }
  if (error) {
    return <Alert severity="warning" sx={{ borderRadius: 2 }}>{error}</Alert>;
  }
  if (!rows || rows.length === 0) {
    return (
      <Box sx={{ backgroundColor: "#FFF", border: "1px solid #E8E8E8", borderRadius: 2, p: 4, textAlign: "center" }}>
        <Typography sx={{ color: "#AAA", fontSize: "0.9rem" }}>No orders are currently awaiting a Proove report.</Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ backgroundColor: "#FFF", border: "1px solid #E8E8E8", borderRadius: 2, overflow: "hidden" }}>
      {rows.map((o) => {
        const isOpen = !!expanded[o.order_id];
        const daysWaiting = o.days_waiting;
        const tier = daysWaiting >= 14 ? "high" : daysWaiting >= 7 ? "mid" : "fresh";
        const daysColors = {
          high: { bg: "#FBE7E5", color: "#b42318" },
          mid: { bg: "#FFF4E0", color: "#8a5a00" },
          fresh: { bg: "#EAF0FB", color: "#1A3A7D" },
        }[tier];
        const clinicianName = [o.clinician_first_name, o.clinician_last_name].filter(Boolean).join(" ").trim() || "Unknown Clinician";
        return (
          <Box key={o.order_id} sx={{ borderTop: "1px solid #F4F4F5", "&:first-of-type": { borderTop: "none" } }}>
            <Box onClick={() => onOpenOrder(o.order_id)} sx={{
              display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.25, cursor: "pointer",
              "&:hover": { backgroundColor: "#FAFAFA" },
            }}>
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); onToggleRow(o.order_id); }}>
                {isOpen ? <ExpandMoreIcon sx={{ fontSize: 18, color: BRAND_NAVY }} /> : <ChevronRightIcon sx={{ fontSize: 18, color: BRAND_NAVY }} />}
              </IconButton>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#111" }}>
                  {o.patient_first_name} {o.patient_last_name} — DOB {o.patient_dob || "—"}
                </Typography>
                <Typography sx={{ fontSize: "0.78rem", color: "#666" }}>
                  {clinicianName} &middot; {o.practice_name || "Unknown Practice"}
                  {" — "}{(o.proove_test_names || []).join(", ") || "Proove test"}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: "0.75rem", color: "#888", whiteSpace: "nowrap" }}>
                Submitted {o.submitted_at ? formatEST(o.submitted_at, { dateOnly: true }) : "—"}
              </Typography>
              <Chip size="small"
                label={daysWaiting === null || daysWaiting === undefined ? "—" : `${daysWaiting} day${daysWaiting === 1 ? "" : "s"} waiting`}
                sx={{ fontSize: "0.68rem", height: 20, bgcolor: daysColors.bg, color: daysColors.color, fontWeight: 600 }} />
              {onUploadReport && (
                <Button size="small" variant="contained"
                  onClick={(e) => onUploadReport(o, e)}
                  sx={{ textTransform: "none", fontSize: 12, bgcolor: BRAND_NAVY, "&:hover": { bgcolor: "#002a5c" }, flexShrink: 0 }}>
                  Upload Report
                </Button>
              )}
            </Box>
            {isOpen && (
              <Box sx={{ px: 2, pb: 2, pl: 5.5 }}>
                <Box sx={{ border: "1px solid #F0F0F0", borderRadius: "8px", p: 1.5, bgcolor: "#FAFBFF" }}>
                  <Typography sx={{ fontSize: 12.5, color: "#444" }}>
                    {o.patient_address_line1} {o.patient_address_unit} &middot; {o.patient_address_city}, {o.patient_address_state} {o.patient_address_zip}
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, color: "#444", mt: 0.5 }}>
                    Ordering NPI: {o.npi || "—"} &middot; Order ID: {o.order_id}
                  </Typography>
                  {o.other_test_ids && o.other_test_ids.length > 0 && (
                    <Typography sx={{ fontSize: 12.5, color: "#444", mt: 0.5 }}>
                      Other tests on this order: {o.other_test_ids.map((tid) => TEST_ID_DISPLAY_NAMES[tid] || tid.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())).join(", ")}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

// ─── Order detail — a "digital copy" of the exact requisition web form ────
// Renders DynamicRequisitionForm in readOnly mode per requisition, so the
// admin/staff viewer sees the SAME section layout, field labels and
// grouping the clinician saw when filling it in, not a reformatted
// summary. Patient demographics/billing + a PDF-download button sit
// alongside it (an addition, never a replacement for the PDF).
// Supporting Documents panel — lists existing docs (type/filename/
// who/when, with a signed preview link), plus an "+ Add Document" row
// of the same shape as the clinician's own OrderTest.js upload control.
// `onUploadSupportingDoc`/`onGetSupportingDocUrl` are optional so this
// dialog still renders fine when neither is supplied (e.g. in a test
// harness that renders it with fixture data only).
function SupportingDocsPanel({ orderId, docs, onUpload, onGetUrl }) {
  const [docType, setDocType] = useState("");
  const [otherLabel, setOtherLabel] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");

  const submit = async () => {
    if (!docType || !file || !onUpload) return;
    setUploading(true); setUploadError("");
    try {
      await onUpload(orderId, { docType, otherLabel, file });
      setDocType(""); setOtherLabel(""); setFile(null);
    } catch (err) {
      setUploadError(extractErrorDetail(err, "Could not upload this document."));
    } finally {
      setUploading(false);
    }
  };

  const view = async (idx) => {
    if (!onGetUrl) return;
    try {
      const url = await onGetUrl(orderId, idx);
      if (url) window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      window.alert(extractErrorDetail(err, "Could not generate a preview link right now."));
    }
  };

  return (
    <Box sx={{ border: "1px solid #E8E8E8", borderRadius: 2, p: 2 }}>
      <SectionHeader>SUPPORTING DOCUMENTS</SectionHeader>
      {(!docs || docs.length === 0) ? (
        <Typography sx={{ fontSize: 13, color: "#999", mb: 1.5 }}>No documents uploaded yet.</Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, mb: 1.5 }}>
          {docs.map((d, idx) => (
            <Box key={idx} sx={{
              display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1,
              px: 1.25, py: 0.75, borderRadius: "8px", border: "1px solid #F0F0F0",
            }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#333" }}>
                  {SUPPORTING_DOC_TYPE_LABELS[d.doc_type] || d.doc_type}
                  {d.doc_type === "other" && d.doc_type_other_label ? ` — ${d.doc_type_other_label}` : ""}
                  {" — "}{d.filename}
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: "#999" }}>
                  Uploaded by {d.uploaded_by_role || "unknown"}{d.uploaded_at ? ` on ${formatEST(d.uploaded_at)}` : ""}
                </Typography>
              </Box>
              <Button size="small" onClick={() => view(idx)} sx={{ textTransform: "none", fontSize: 12, flexShrink: 0 }}>
                View
              </Button>
            </Box>
          ))}
        </Box>
      )}
      {onUpload && (
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
          <Select size="small" displayEmpty value={docType} onChange={(e) => setDocType(e.target.value)}
            sx={{ minWidth: 160, fontSize: 13 }}>
            <MenuItem value="" disabled><em>Document type</em></MenuItem>
            {SUPPORTING_DOC_TYPE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
          {docType === "other" && (
            <TextField size="small" placeholder="Describe this document" value={otherLabel}
              onChange={(e) => setOtherLabel(e.target.value)} sx={{ minWidth: 170 }} />
          )}
          <Button component="label" size="small" variant="outlined" sx={{ textTransform: "none", fontSize: 12.5 }}>
            {file ? file.name : "Choose file"}
            <input type="file" hidden accept="application/pdf,image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </Button>
          <Button size="small" variant="contained" disabled={!docType || !file || uploading}
            onClick={submit} sx={{ textTransform: "none", bgcolor: BRAND_NAVY, "&:hover": { bgcolor: "#002a5c" } }}>
            {uploading ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : "+ Add Document"}
          </Button>
        </Box>
      )}
      {uploadError && <Alert severity="warning" sx={{ mt: 1 }}>{uploadError}</Alert>}
    </Box>
  );
}

// ─── Inline preview pane ────────────────────────────────────────────────────
// Same proven preview pattern ReportsDashboard.js's own PreviewPanel uses
// (signed URL fetched on demand -> rendered in an iframe), just ported
// into a toggleable panel under each requisition's Download PDF button
// instead of a permanent side-panel (this dialog has no second column to
// put one in). `kind` branches to an <img> instead of an <iframe> for an
// image-type file — unused by the requisition call site below (every
// requisition PDF this app renders is, in fact, a PDF — see
// _requisition_pdf_blob_name server-side), but kept generic so this same
// panel can preview an image-type Supporting Document later without a
// rewrite.
// Signed URL lifetime server-side is 30 minutes (main.py's requisition
// pdf-url endpoint, `expiration=timedelta(minutes=30)`). SAFE_URL_LIFETIME_MS
// is the age at which we proactively re-fetch on toggle-open rather than
// reuse a cached URL (a safety margin under the real expiry below).
// REAL_URL_LIFETIME_MS schedules a client-side "this has now expired"
// fallback for a preview left open past the real expiry, since an expired
// signed URL returns a provider error page inside the iframe rather than
// firing a usable cross-origin onError.
const SAFE_URL_LIFETIME_MS = 25 * 60 * 1000;
const REAL_URL_LIFETIME_MS = 30 * 60 * 1000;

function InlinePreviewFrame({ url, kind, onError }) {
  const [loaded, setLoaded] = useState(false);

  // Fallback fade-in — some PDF-viewer/browser combinations don't fire
  // onLoad reliably inside an iframe, which would otherwise leave the
  // frame stuck at opacity 0 forever.
  useEffect(() => {
    setLoaded(false);
    const t = setTimeout(() => setLoaded(true), 4000);
    return () => clearTimeout(t);
  }, [url]);

  // The placeholder background itself stays at full opacity (that's the
  // point of it — no blank-white flash while the PDF loads); only the
  // iframe/img content fades in once it has actually loaded, via onLoad
  // (or the 4s fallback above for browsers that don't fire it reliably).
  const frameSx = {
    mt: 1, borderRadius: "12px", overflow: "hidden",
    height: "min(600px, 60vh)",
    boxShadow: "0 8px 40px rgba(0,0,0,0.12)", border: "1px solid rgba(0,53,122,0.08)",
    bgcolor: "#f4f7ff",
  };
  const fadeStyle = { opacity: loaded ? 1 : 0, transition: "opacity 0.25s ease" };

  if (kind === "image") {
    return (
      <Box sx={{ ...frameSx, overflow: "auto", textAlign: "center", p: 1 }}>
        <img
          key={url} src={url} alt="Document preview"
          style={{ maxWidth: "100%", height: "auto", display: "block", margin: "0 auto", ...fadeStyle }}
          onLoad={() => setLoaded(true)} onError={onError}
        />
      </Box>
    );
  }
  return (
    <Box sx={{ ...frameSx, display: "flex" }}>
      <iframe
        key={url} src={url} title="Requisition Preview"
        style={{ width: "100%", height: "100%", border: "none", ...fadeStyle }}
        onLoad={() => setLoaded(true)} onError={onError}
      />
    </Box>
  );
}

// Preview + Download, paired in one header row — "Preview" toggle sits
// next to the Download PDF button rather than in a separate box below it,
// and owns the signed-URL caching/expiry state for both.
function RequisitionHeaderRow({ title, orderId, schemaKey, onDownloadPdf, onPreviewPdf }) {
  const [open, setOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewFetchedAt, setPreviewFetchedAt] = useState(null);
  const [loadingUrl, setLoadingUrl] = useState(false);
  const [error, setError] = useState("");
  const [expired, setExpired] = useState(false);
  const expiryTimerRef = useRef(null);

  useEffect(() => () => { if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current); }, []);

  const fetchPreview = async () => {
    setLoadingUrl(true); setError(""); setExpired(false);
    if (expiryTimerRef.current) { clearTimeout(expiryTimerRef.current); expiryTimerRef.current = null; }
    try {
      const url = await onPreviewPdf(orderId, schemaKey);
      if (url) {
        setPreviewUrl(url);
        setPreviewFetchedAt(Date.now());
        // Client-side fallback: if the panel is left open past the real
        // 30-minute signed-URL expiry, surface the "expired" affordance
        // instead of leaving a silently-broken frame up.
        expiryTimerRef.current = setTimeout(() => setExpired(true), REAL_URL_LIFETIME_MS);
      } else {
        setError("Preview not available.");
      }
    } catch (err) {
      setError(extractErrorDetail(err, "Could not load preview right now."));
    } finally {
      setLoadingUrl(false);
    }
  };

  const toggle = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    const isStale = !previewUrl || expired || !previewFetchedAt || (Date.now() - previewFetchedAt) > SAFE_URL_LIFETIME_MS;
    if (isStale && !loadingUrl) await fetchPreview();
  };

  return (
    <Box sx={{ mb: open ? 1 : 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
        <SectionHeader>{title}</SectionHeader>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button size="small" startIcon={<DownloadIcon sx={{ fontSize: 15 }} />}
            onClick={() => onDownloadPdf(orderId, schemaKey)} sx={{ textTransform: "none" }}>
            Download PDF
          </Button>
          {onPreviewPdf && (
            <Button size="small" startIcon={<VisibilityIcon sx={{ fontSize: 15 }} />}
              onClick={toggle} sx={{ textTransform: "none" }}>
              {open ? "Hide Preview" : "Preview"}
            </Button>
          )}
        </Box>
      </Box>
      {open && (
        loadingUrl ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress size={28} sx={{ color: BRAND_NAVY }} /></Box>
        ) : error ? (
          <Alert severity="warning" sx={{ mt: 1 }}>{error}</Alert>
        ) : previewUrl ? (
          <>
            {/* A PDF already loaded in the iframe stays viewable even after
                its signed URL expires server-side — this alert is just the
                affordance for a frame that genuinely breaks mid-view (or
                for the client-side expiry timer firing while left open);
                it sits above the still-mounted frame rather than replacing
                it, so a working preview is never torn down out from under
                someone still reading it. */}
            {expired && (
              <Alert severity="warning" sx={{ mt: 1, mb: 1 }}
                action={<Button size="small" onClick={fetchPreview} sx={{ textTransform: "none" }}>Reload</Button>}>
                Preview expired — click to reload.
              </Alert>
            )}
            <InlinePreviewFrame url={previewUrl} kind="pdf" onError={() => setExpired(true)} />
          </>
        ) : null
      )}
    </Box>
  );
}

// ─── Saved Test Results — Phase 3 of the "My Results" ecosystem plan ──────
// Admin-only (the backend route this reads, /api/admin/patient-records/
// {patient_id}/test-results, deliberately isn't mirrored to staff — see
// that route's own comment). Shows a patient's ENTIRE report_summaries
// history across every lab/report type, not just Proove-linked orders —
// a distinct data source from the Order tree this dialog is opened from.
// Content layout mirrors ClientFacingDashboard.js's own extracted-info
// panel (ai_summary via dangerouslySetInnerHTML, stats/structured/clinical
// flags as a JSON block, abnormal_value_insights/citations as lists)
// adapted to this file's light "bordered card" visual language instead of
// that dialog's dark theme, for consistency with the rest of this page.
// Every field the backend can return is rendered here, including
// clinical_flags (downstream-mismatch-checker finding — it was fetched/
// decrypted/decrypt-counted server-side but had no renderer here at
// first) — and the "(unavailable)" decrypt-failure sentinel each field
// can carry is shown as an explicit failure notice rather than silently
// printing as real data (stats/flags) or silently vanishing (the
// array-shaped fields), so a genuine decrypt corruption never looks
// identical to "no data was ever saved."
function reportTypeDisplayLabel(rtid) {
  if (!rtid) return "Report";
  return TEST_ID_DISPLAY_NAMES[rtid] || rtid.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// The backend's decrypt helpers return the literal string "(unavailable)"
// only for a genuinely corrupted/key-mismatched field (never for "no data
// saved") — see admin_patient_test_results's own _dec_maybe_json in
// main.py. Must be checked explicitly everywhere a field is rendered,
// otherwise it either prints as if it were real extracted data (inside a
// JSON <pre> block) or silently vanishes (an Array.isArray check on a
// string is false), both of which hide a real decrypt failure from the
// admin instead of surfacing it.
const DECRYPT_UNAVAILABLE = "(unavailable)";

function DecryptFailureNotice({ label }) {
  return (
    <Typography sx={{ fontSize: 12, color: "#b42318", fontStyle: "italic" }}>
      {label} could not be decrypted (key mismatch or data corruption) — contact engineering.
    </Typography>
  );
}

// 2026-10-24 — readable, field-aware rendering for the 3 Proove-typed
// report shapes (same field names as labariumaiportal's own MyResults.js/
// MyPatientsResults.js PgxSection/ScoreStrip — kept in sync by convention,
// not a shared import, since this is a separate frontend app). Replaces
// a raw JSON.stringify dump, per direct request — an admin reviewing a
// patient's test results should see labeled values, not a JSON blob.
// Any report_type_id this doesn't recognize (native tox/blood/molecular,
// or a genuinely new type) falls back to the original JSON block below,
// so nothing silently stops rendering just because this component
// doesn't have a dedicated layout for it yet.
const _PROOVE_SCORE_FIELDS = {
  proove_opioid_risk: [
    { field: "proove_ori", label: "Opioid Risk Index" },
    { field: "ort", label: "Opioid Risk Tool (ORT)" },
    { field: "phq2", label: "PHQ-2" },
  ],
  proove_pain_perception: [
    { field: "self_reported_pain", label: "Self-Reported Pain" },
    { field: "pss", label: "Perceived Stress Scale" },
  ],
};

function AdminStatTile({ label, value }) {
  // ui-ux-checker finding — `value || {}` collapsed a real, clinically
  // meaningful score of 0 ("no risk") to {} (0 is falsy in JS), silently
  // dropping the tile instead of showing "Score: 0". Check the type
  // explicitly instead of relying on truthiness.
  const d = (value && typeof value === "object") ? value : {};
  const parts = [];
  if (d.score != null) parts.push(`Score: ${d.score}`);
  if (d.level) parts.push(`Level: ${d.level}`);
  if (d.range) parts.push(`Range: ${d.range}`);
  if (!parts.length) return null;
  return (
    <Box sx={{ border: "1px solid #EEE", borderRadius: "6px", px: 1.25, py: 0.75, minWidth: 140 }}>
      <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#888" }}>{label.toUpperCase()}</Typography>
      <Typography sx={{ fontSize: 13, color: "#222" }}>{parts.join(" · ")}</Typography>
    </Box>
  );
}

// 2026-10-24 — Labarium-NATIVE reports (tox/drug_screen/blood/pcr panels),
// per the user's own correction: this app isn't just Proove reports. The
// real shape comes straight from _build_structured_results_payload
// (main.py ~5602) — `kind` is "tox" | "drug_screen" | "standard", each
// with its own row-list fields (flagged/full_results/etc.), and each row
// shares the SAME test/value/unit/reference_range/status columns
// report_builder.py already renders for the PDF (per that function's own
// docstring) — but row shape genuinely varies by panel type (tox rows
// also carry medication_comparison/comment), so this renders whatever
// columns a row actually has rather than hardcoding one fixed column
// set per kind.
// ui-ux-checker findings (2026-10-24), both fixed:
// 1. ONE global field order never matched 4 of the 6 real sub-table row
//    shapes (tox's first_page_summary/second_section and drug_screen's
//    consistent/validity use entirely different Title-Case keys from
//    ocr_engine.py's own flatten_tox_summary_first/flatten_drug_screen_*,
//    and tox's own full_results — flatten_tox_detail — has section/conc/
//    cutoff, not unit/reference_range). Each call site below now passes
//    its OWN real field order (or none, for the two shapes whose keys
//    are already human-readable and should just print in natural order).
// 2. No leading-field bold, unlike AdminGeneTable's identical pattern
//    just below — first field is now bolded here too.
function AdminResultsRow({ row, fieldOrder }) {
  const safe = (row && typeof row === "object") ? row : {};
  const order = fieldOrder && fieldOrder.length ? fieldOrder : Object.keys(safe);
  const known = order.filter((k) => safe[k] != null && safe[k] !== "");
  const extra = fieldOrder && fieldOrder.length
    ? Object.keys(safe).filter((k) => !fieldOrder.includes(k) && safe[k] != null && safe[k] !== "")
    : [];
  const fields = [...known, ...extra];
  if (!fields.length) {
    return <Typography sx={{ fontSize: 12.5, color: "#333", py: 0.25, borderBottom: "1px solid #F5F5F5" }}>—</Typography>;
  }
  const [lead, ...rest] = fields;
  return (
    <Typography sx={{ fontSize: 12.5, color: "#333", py: 0.25, borderBottom: "1px solid #F5F5F5" }}>
      <b>{lead.replace(/_/g, " ")}: {safe[lead]}</b>
      {rest.map((k) => ` · ${k.replace(/_/g, " ")}: ${safe[k]}`).join("")}
    </Typography>
  );
}

function AdminResultsTable({ title, rows, badge, fieldOrder }) {
  if (!Array.isArray(rows) || !rows.length) return null;
  return (
    <Box sx={{ mt: 1 }}>
      <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#888", mb: 0.25 }}>
        {/* ui-ux-checker finding — rows.length and total_count are always
            the same number for "Full Results" (both derived from the
            same full list), so showing both repeated the count; drop the
            bare count whenever a badge (which already states the total)
            is present. */}
        {title.toUpperCase()}{badge ? ` — ${badge}` : ` (${rows.length})`}
      </Typography>
      {/* ui-ux-checker finding — no scroll containment, unlike this
          file's own JSON-fallback sibling (maxHeight: 220, overflow:
          auto). A real tox/drug panel's full_results can be 30-50+ rows
          and would otherwise balloon the whole admin card vertically. */}
      <Box sx={{ maxHeight: 280, overflowY: "auto" }}>
        {rows.map((row, i) => <AdminResultsRow key={i} row={row} fieldOrder={fieldOrder} />)}
      </Box>
    </Box>
  );
}

// Real field orders, traced from ocr_engine.py's own DataFrame builders
// (flatten_standard_tests/flatten_tox_detail) — leading field first so
// AdminResultsRow's bold lead is the field that actually matters most
// for a quick scan.
const _STANDARD_ROW_ORDER = ["test", "value", "unit", "reference_range", "status"];
const _TOX_DETAIL_ROW_ORDER = ["section", "test", "conc", "cutoff", "medication_comparison", "comment"];

function NativeStructuredResultsSection({ structured }) {
  if (!structured || typeof structured !== "object") return null;
  const countsBadge = structured.total_count != null
    ? `${structured.flagged_count ?? 0} of ${structured.total_count} flagged`
    : null;

  if (structured.kind === "tox") {
    return (
      <Box>
        {/* first_page_summary/second_section (flatten_tox_summary_first)
            use their own already-readable Title-Case keys — no fixed
            order imposed, print in natural (payload) order. */}
        <AdminResultsTable title="First Page Summary" rows={structured.first_page_summary} />
        <AdminResultsTable title="Second Section" rows={structured.second_section} />
        <AdminResultsTable title="Full Results" rows={structured.full_results} badge={countsBadge} fieldOrder={_TOX_DETAIL_ROW_ORDER} />
      </Box>
    );
  }
  if (structured.kind === "drug_screen") {
    return (
      <Box>
        {/* consistent/validity (flatten_drug_screen_*) — same reasoning
            as tox's own summary tables above: natural order. */}
        <AdminResultsTable title="Consistent" rows={structured.consistent} />
        <AdminResultsTable title="Validity" rows={structured.validity} />
        {structured.validity_note && (
          <Typography sx={{ fontSize: 12.5, color: "#444", fontStyle: "italic", mt: 0.5 }}>{structured.validity_note}</Typography>
        )}
        <AdminResultsTable title="Full Results" rows={structured.full_results} badge={countsBadge} fieldOrder={_STANDARD_ROW_ORDER} />
      </Box>
    );
  }
  if (structured.kind === "standard") {
    return (
      <Box>
        <AdminResultsTable title="Flagged" rows={structured.flagged} fieldOrder={_STANDARD_ROW_ORDER} />
        <AdminResultsTable title="Full Results" rows={structured.full_results} badge={countsBadge} fieldOrder={_STANDARD_ROW_ORDER} />
      </Box>
    );
  }
  return null; // unrecognized kind — caller falls back to the raw JSON block
}

function AdminGeneTable({ genes }) {
  if (!Array.isArray(genes) || !genes.length) return null;
  return (
    <Box sx={{ mt: 1 }}>
      <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#888", mb: 0.5 }}>GENE FINDINGS ({genes.length})</Typography>
      {genes.map((g, i) => (
        <Typography key={i} sx={{ fontSize: 12.5, color: "#333", py: 0.25 }}>
          <b>{g.gene || "—"}</b>{g.rs_number ? ` (${g.rs_number})` : ""}: {g.genotype || g.result || "—"}
          {g.phenotype ? ` — ${g.phenotype}` : ""}
        </Typography>
      ))}
    </Box>
  );
}

function ExtractedStatsSection({ rtid, stats }) {
  if (!stats || typeof stats !== "object") return null;

  if (rtid === "proove_pgx") {
    const genes = Array.isArray(stats.genes) ? stats.genes : [];
    const riskMgmt = Array.isArray(stats.risk_management) ? stats.risk_management : [];
    return (
      <Box>
        <AdminGeneTable genes={genes} />
        {stats.current_patient_medications && (
          <Box sx={{ mt: 1 }}>
            <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#888", mb: 0.25 }}>FLAGGED MEDICATIONS</Typography>
            <Typography sx={{ fontSize: 12.5, color: "#333" }}>{stats.current_patient_medications}</Typography>
          </Box>
        )}
        {riskMgmt.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#888", mb: 0.25 }}>RISK MANAGEMENT FINDINGS</Typography>
            {riskMgmt.map((r, i) => (
              <Box key={i} sx={{ py: 0.25 }}>
                <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: "#222" }}>{r.condition || "—"}</Typography>
                <Typography sx={{ fontSize: 12.5, color: "#444" }}>{r.finding || "—"}</Typography>
              </Box>
            ))}
          </Box>
        )}
        {stats.dosing_guidance_summary && (
          <Box sx={{ mt: 1 }}>
            <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#888", mb: 0.25 }}>DOSING GUIDANCE</Typography>
            <Typography sx={{ fontSize: 12.5, color: "#333" }}>{stats.dosing_guidance_summary}</Typography>
          </Box>
        )}
        {!genes.length && !riskMgmt.length && !stats.current_patient_medications && !stats.dosing_guidance_summary && (
          <Typography sx={{ fontSize: 12.5, color: "#888", fontStyle: "italic" }}>No drug-metabolism findings were extracted from this report.</Typography>
        )}
      </Box>
    );
  }

  const scoreFields = _PROOVE_SCORE_FIELDS[rtid];
  if (scoreFields) {
    const comt = rtid === "proove_pain_perception" ? stats.comt_predisposition : null;
    const hasComt = !!(comt && comt.level);
    const hasAnyScore = scoreFields.some((f) => {
      const d = stats[f.field];
      return d && typeof d === "object" && (d.score != null || d.level);
    });
    const hasGenotypes = Array.isArray(stats.genotypes) && stats.genotypes.length > 0;
    const hasForeignScores = Array.isArray(stats.foreign_scores) && stats.foreign_scores.length > 0;
    const hasHistory = Array.isArray(stats.history_questions) && stats.history_questions.length > 0;
    // ui-ux-checker finding — this branch had no "nothing extracted"
    // fallback, unlike the pgx branch just above; an admin would see a
    // bare "EXTRACTED STATS" header with nothing underneath instead of
    // an explicit, reassuring "nothing was extracted" message.
    if (!hasAnyScore && !hasComt && !hasGenotypes && !hasForeignScores && !hasHistory) {
      return (
        <Typography sx={{ fontSize: 12.5, color: "#888", fontStyle: "italic" }}>
          No scored findings were extracted from this report.
        </Typography>
      );
    }
    return (
      <Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {scoreFields.map((f) => <AdminStatTile key={f.field} label={f.label} value={stats[f.field]} />)}
          {hasComt && <AdminStatTile label="Genetic Pain Sensitivity (COMT)" value={{ level: comt.level }} />}
        </Box>
        <AdminGeneTable genes={stats.genotypes} />
        {hasForeignScores && (
          <Box sx={{ mt: 1 }}>
            <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#888", mb: 0.25 }}>OTHER SCORES</Typography>
            {stats.foreign_scores.map((s, i) => (
              <Typography key={i} sx={{ fontSize: 12.5, color: "#333" }}>{s.label}: {s.value}</Typography>
            ))}
          </Box>
        )}
        {hasHistory && (
          <Box sx={{ mt: 1 }}>
            <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#888", mb: 0.25 }}>HISTORY QUESTIONS</Typography>
            {stats.history_questions.map((h, i) => (
              <Typography key={i} sx={{ fontSize: 12.5, color: "#333" }}>{h.question}: {h.answer ?? "—"}</Typography>
            ))}
          </Box>
        )}
      </Box>
    );
  }

  // Labarium-native reports (tox/drug_screen/blood/pcr panels) — real
  // shape keyed by `kind`, built from the actual backend payload shape
  // (_build_structured_results_payload), not guessed.
  const nativeSection = NativeStructuredResultsSection({ structured: stats });
  if (nativeSection) return nativeSection;

  // Genuinely unrecognized shape (a report type/structure this component
  // doesn't have a layout for yet) — fall back to the raw JSON block
  // rather than rendering nothing.
  return (
    <Box component="pre" sx={{
      fontSize: 11.5, color: "#444", bgcolor: "#FAFAFA", borderRadius: "6px", p: 1.25, m: 0,
      overflow: "auto", maxHeight: 220, whiteSpace: "pre-wrap",
    }}>
      {JSON.stringify(stats, null, 2)}
    </Box>
  );
}

function TestResultCard({ row }) {
  const [open, setOpen] = useState(false);
  const statsUnavailable = row.stats_json === DECRYPT_UNAVAILABLE || row.structured_results === DECRYPT_UNAVAILABLE;
  const flagsUnavailable = row.clinical_flags === DECRYPT_UNAVAILABLE;
  const abnormalUnavailable = row.abnormal_value_insights === DECRYPT_UNAVAILABLE;
  const followupUnavailable = row.followup_test_suggestions === DECRYPT_UNAVAILABLE;
  const citationsUnavailable = row.citations === DECRYPT_UNAVAILABLE;
  const hasAnyExtracted = row.ai_summary
    || (row.stats_json && row.stats_json !== DECRYPT_UNAVAILABLE) || (row.structured_results && row.structured_results !== DECRYPT_UNAVAILABLE)
    || (row.clinical_flags && row.clinical_flags !== DECRYPT_UNAVAILABLE)
    || (Array.isArray(row.abnormal_value_insights) && row.abnormal_value_insights.length > 0)
    || (Array.isArray(row.citations) && row.citations.length > 0)
    || (Array.isArray(row.followup_test_suggestions) && row.followup_test_suggestions.length > 0)
    || statsUnavailable || flagsUnavailable || abnormalUnavailable || followupUnavailable || citationsUnavailable;
  return (
    <Box sx={{ border: "1px solid #E8E8E8", borderRadius: 2, overflow: "hidden" }}>
      <Box onClick={() => setOpen((v) => !v)} sx={{
        display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.25, cursor: "pointer",
        "&:hover": { backgroundColor: "#FAFAFA" },
      }}>
        {open ? <ExpandMoreIcon sx={{ fontSize: 18, color: BRAND_NAVY }} /> : <ChevronRightIcon sx={{ fontSize: 18, color: BRAND_NAVY }} />}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#111" }}>
            {reportTypeDisplayLabel(row.report_type_id)}
          </Typography>
          <Typography sx={{ fontSize: "0.78rem", color: "#666" }}>
            {row.lab_id || "Unknown Lab"} &middot; {row.practice_name || "Unknown Practice"}
            {row.provider_name ? ` (${row.provider_name}${row.provider_npi ? `, NPI ${row.provider_npi}` : ""})` : ""}
          </Typography>
        </Box>
        <Chip size="small" label={row.report_kind || "—"} sx={{ fontSize: "0.65rem", height: 20, bgcolor: "#FFF4E0", color: "#8a5a00" }} />
        <Typography sx={{ fontSize: "0.75rem", color: "#888", whiteSpace: "nowrap" }}>
          {row.generated_at ? formatEST(row.generated_at, { dateOnly: true }) : "—"}
        </Typography>
      </Box>
      {open && (
        <Box sx={{ px: 2, pb: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
          {row.provider_phone && (
            <Typography sx={{ fontSize: "0.78rem", color: "#666" }}>
              Provider callback phone: <b>{row.provider_phone}</b>
            </Typography>
          )}
          {row.ai_summary && (
            <Box sx={{ border: "1px solid #F0F0F0", borderRadius: "8px", p: 1.5, bgcolor: "#FAFBFF" }}>
              <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#888", mb: 0.5 }}>AI SUMMARY</Typography>
              <Box sx={{ fontSize: 13, color: "#333", lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: row.ai_summary }} />
            </Box>
          )}
          {statsUnavailable ? (
            <Box sx={{ border: "1px solid #F0F0F0", borderRadius: "8px", p: 1.5 }}>
              <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#888", mb: 0.5 }}>EXTRACTED STATS</Typography>
              <DecryptFailureNotice label="Extracted stats" />
            </Box>
          ) : (row.stats_json || row.structured_results) ? (
            <Box sx={{ border: "1px solid #F0F0F0", borderRadius: "8px", p: 1.5 }}>
              <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#888", mb: 0.5 }}>EXTRACTED STATS</Typography>
              <ExtractedStatsSection rtid={row.report_type_id} stats={row.stats_json || row.structured_results} />
            </Box>
          ) : null}
          {flagsUnavailable ? (
            <Box sx={{ border: "1px solid #F0F0F0", borderRadius: "8px", p: 1.5 }}>
              <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#888", mb: 0.5 }}>CLINICAL FLAGS</Typography>
              <DecryptFailureNotice label="Clinical flags" />
            </Box>
          ) : (row.clinical_flags && typeof row.clinical_flags === "object") ? (
            <Box sx={{ border: "1px solid #F0F0F0", borderRadius: "8px", p: 1.5 }}>
              <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#888", mb: 0.5 }}>CLINICAL FLAGS</Typography>
              <Box component="pre" sx={{
                fontSize: 11.5, color: "#444", bgcolor: "#FAFAFA", borderRadius: "6px", p: 1.25, m: 0,
                overflow: "auto", maxHeight: 180, whiteSpace: "pre-wrap",
              }}>
                {JSON.stringify(row.clinical_flags, null, 2)}
              </Box>
            </Box>
          ) : null}
          {abnormalUnavailable ? (
            <Box sx={{ border: "1px solid #F0F0F0", borderRadius: "8px", p: 1.5 }}>
              <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#888", mb: 0.5 }}>FLAGGED VALUES</Typography>
              <DecryptFailureNotice label="Flagged values" />
            </Box>
          ) : Array.isArray(row.abnormal_value_insights) && row.abnormal_value_insights.length > 0 ? (
            <Box sx={{ border: "1px solid #F0F0F0", borderRadius: "8px", p: 1.5 }}>
              <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#888", mb: 0.5 }}>FLAGGED VALUES</Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                {row.abnormal_value_insights.map((item, i) => (
                  <Box key={i} sx={{ p: 1, borderRadius: "6px", bgcolor: "#FFF8E5" }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: "#8a5a00" }}>
                      {item.value} {item.unit} ({item.flag_type})
                    </Typography>
                    {item.what_it_means_for_you && (
                      <Typography sx={{ fontSize: 12, color: "#555", mt: 0.25 }}>{item.what_it_means_for_you}</Typography>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          ) : null}
          {followupUnavailable ? (
            <Box sx={{ border: "1px solid #F0F0F0", borderRadius: "8px", p: 1.5 }}>
              <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#888", mb: 0.5 }}>FOLLOW-UP SUGGESTIONS</Typography>
              <DecryptFailureNotice label="Follow-up suggestions" />
            </Box>
          ) : Array.isArray(row.followup_test_suggestions) && row.followup_test_suggestions.length > 0 ? (
            <Box sx={{ border: "1px solid #F0F0F0", borderRadius: "8px", p: 1.5 }}>
              <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#888", mb: 0.5 }}>FOLLOW-UP SUGGESTIONS</Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                {row.followup_test_suggestions.map((s, i) => (
                  <Typography key={i} sx={{ fontSize: 12.5, color: "#555" }}>
                    {typeof s === "string" ? s : JSON.stringify(s)}
                  </Typography>
                ))}
              </Box>
            </Box>
          ) : null}
          {citationsUnavailable ? (
            <Box sx={{ border: "1px solid #F0F0F0", borderRadius: "8px", p: 1.5 }}>
              <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#888", mb: 0.5 }}>CITATIONS</Typography>
              <DecryptFailureNotice label="Citations" />
            </Box>
          ) : Array.isArray(row.citations) && row.citations.length > 0 ? (
            <Box sx={{ border: "1px solid #F0F0F0", borderRadius: "8px", p: 1.5 }}>
              <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#888", mb: 0.5 }}>CITATIONS</Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                {row.citations.map((c, i) => (
                  <Typography key={i} sx={{ fontSize: 12, color: "#777" }}>{typeof c === "string" ? c : JSON.stringify(c)}</Typography>
                ))}
              </Box>
            </Box>
          ) : null}
          {!hasAnyExtracted && (
            <Typography sx={{ fontSize: 12.5, color: "#999", textAlign: "center", py: 1 }}>
              No summary or extracted data is available for this report.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

function PatientTestResultsDialog({ open, onClose, patientLabel, rows, loading, error }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography sx={{ fontWeight: 700, color: BRAND_NAVY }}>
          Saved Test Results — {patientLabel || ""}
        </Typography>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress sx={{ color: BRAND_NAVY }} /></Box>
        ) : error ? (
          <Alert severity="warning">{error}</Alert>
        ) : rows.length === 0 ? (
          <Typography sx={{ fontSize: 13.5, color: "#999", textAlign: "center", py: 3 }}>
            No saved test results found for this patient.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {rows.map((row) => <TestResultCard key={row._id} row={row} />)}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export function OrderDetailDialog({ open, onClose, detail, loading, error, onDownloadPdf, onPreviewPdf, onUploadSupportingDoc, onGetSupportingDocUrl, onUploadProoveReport }) {
  const requisitions = detail?.requisitions || [];
  // Shared sections (ordering_provider/patient_info/payment_info) are
  // confirmed byte-identical submitted_data across every requisition in
  // the same order, so any one requisition's copy is authoritative —
  // render them ONCE here, from the first requisition, instead of once
  // per family card (that was Bug 2: a 2+-family order showed "Ordering
  // Provider Information"/"Patient Information"/"Payment Information"
  // duplicated once per family).
  // Prefer a requisition whose submitted_data actually decrypted — the
  // shared fields are identical across every requisition in the order,
  // so one requisition's decrypt failure shouldn't blank the shared
  // block when a sibling requisition has the same data intact.
  const firstReq = requisitions.find((r) => !r.decrypt_error) || requisitions[0];
  const sharedSchema = firstReq ? (firstReq.schema || []).filter((s) => SHARED_SECTION_IDS.includes(s.id)) : [];
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography sx={{ fontWeight: 700, color: BRAND_NAVY }}>
          Order {detail?.order_id}
        </Typography>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress sx={{ color: BRAND_NAVY }} /></Box>
        ) : error ? (
          <Alert severity="warning">{error}</Alert>
        ) : detail && detail.patient ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {detail.awaiting_proove_report && !detail.proove_report_delivered && (
              <Alert severity="info" sx={{ borderRadius: 2 }}
                action={onUploadProoveReport && (
                  <Button size="small" variant="contained" onClick={onUploadProoveReport}
                    sx={{ textTransform: "none", bgcolor: BRAND_NAVY, "&:hover": { bgcolor: "#002a5c" } }}>
                    Upload Report
                  </Button>
                )}>
                This order is awaiting a Proove report upload.
              </Alert>
            )}
            <Box sx={{ border: "1px solid #E8E8E8", borderRadius: 2, p: 2 }}>
              <SectionHeader>PATIENT & ORDER</SectionHeader>
              <Typography sx={{ fontSize: 13.5 }}>
                {detail.patient.first_name} {detail.patient.last_name} &mdash; DOB {detail.patient.dob}
              </Typography>
              <Typography sx={{ fontSize: 13, color: "#666" }}>
                {detail.patient.address_line1} {detail.patient.address_unit} &middot; {detail.patient.address_city}, {detail.patient.address_state} {detail.patient.address_zip}
              </Typography>
              <Typography sx={{ fontSize: 13, color: "#666" }}>{detail.patient.email} &middot; {detail.patient.phone}</Typography>
              <Typography sx={{ fontSize: 13, color: "#666", mt: 1 }}>
                Lab: {detail.lab} &middot; Practice: {detail.practice_name} (NPI {detail.npi}) &middot; Status: {ORDER_STATUS_DISPLAY[detail.status] || detail.status}
              </Typography>
              <Typography sx={{ fontSize: 13, color: "#666" }}>
                Submitted: {detail.submitted_at ? formatEST(detail.submitted_at) : "—"} &middot;
                Patient consent: {String(detail.patient_consent ?? "not yet given")}
                {/* 2026-10-18 fix (frontend-backend contract sweep) —
                    patient_consent_status is a distinct string field
                    (e.g. pending/declined/given) the backend already sends
                    here, but only the plain boolean patient_consent was
                    ever rendered, hiding that nuance from staff. */}
                {detail.patient_consent_status ? ` (${detail.patient_consent_status})` : ""} &middot;
                {detail.biobank_consent === true ? "Biobank: opted in"
                  : detail.biobank_consent === false ? "Biobank: not opted in"
                  : detail.patient_consent === true ? "Biobank: not applicable to this order"
                  : "Biobank: pending patient decision"}
              </Typography>
            </Box>

            {sharedSchema.length > 0 && (
              <Box sx={{ border: "1px solid #E8E8E8", borderRadius: 2, p: 2 }}>
                <SectionHeader>SHARED ORDER INFORMATION</SectionHeader>
                {firstReq.decrypt_error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    This requisition's submitted data failed to decrypt and could not be displayed.
                    This is a data error, not an empty submission — do not treat it as the clinician
                    submitting nothing. Contact engineering/support before relying on this section.
                  </Alert>
                )}
                <DynamicRequisitionForm schema={sharedSchema} data={firstReq.submitted_data || {}} readOnly onChange={() => {}} />
              </Box>
            )}

            {requisitions.map((r) => {
              // Family-specific sections only — the shared ones above
              // (ordering_provider/patient_info/payment_info) are already
              // rendered once for the whole order, never re-shown here.
              // Used only to decide whether there's anything left to
              // render per requisition — the actual filtering below is
              // done by DynamicRequisitionForm's own excludeSectionIds
              // prop, never duplicated here.
              const familySchema = (r.schema || []).filter((s) => !SHARED_SECTION_IDS.includes(s.id));
              return (
                <Box key={r.schema_key} sx={{ border: "1px solid #E8E8E8", borderRadius: 2, p: 2 }}>
                  <RequisitionHeaderRow
                    title={<>REQUISITION &mdash; {(r.test_ids || []).map((tid) => TEST_ID_DISPLAY_NAMES[tid] || tid.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())).join(", ")}</>}
                    orderId={detail.order_id} schemaKey={r.schema_key}
                    onDownloadPdf={onDownloadPdf} onPreviewPdf={onPreviewPdf}
                  />
                  {/* 2026-10-18 fix (frontend-backend contract sweep) —
                      provider_signed_at is returned per requisition but was
                      never surfaced, so staff had no way to see when the
                      provider actually signed this specific requisition. */}
                  <Typography sx={{ fontSize: 12, color: "#999", mt: -1, mb: 1 }}>
                    Provider signed: {r.provider_signed_at ? formatEST(r.provider_signed_at) : "not yet signed"}
                  </Typography>
                  {r.decrypt_error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      This requisition's submitted data failed to decrypt and could not be displayed.
                      This is a data error, not an empty submission — do not treat it as the clinician
                      submitting nothing. Contact engineering/support before relying on this section.
                    </Alert>
                  )}
                  {familySchema.length > 0 ? (
                    <DynamicRequisitionForm schema={r.schema} excludeSectionIds={SHARED_SECTION_IDS} data={r.submitted_data || {}} readOnly onChange={() => {}} />
                  ) : (
                    <Typography sx={{ fontSize: 13, color: "#999" }}>No family-specific fields on this requisition.</Typography>
                  )}
                </Box>
              );
            })}

            <SupportingDocsPanel
              orderId={detail.order_id}
              docs={detail.supporting_documents}
              onUpload={onUploadSupportingDoc}
              onGetUrl={onGetSupportingDocUrl}
            />
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
